import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { NotificationBell } from '../features/notifications/NotificationBell'
import { TourProvider } from '../features/onboarding/TourProvider'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { LanguageSwitcher } from './LanguageSwitcher'

const ACTIVE_ORG_KEY = 'ohear.active-org-id'

interface MeOrg {
  id: string
  name: string
  slug: string | null
  role: string
  current: boolean
}

interface LayoutProps {
  children: ReactNode
  /** Number of recordings still pending upload (shown next to the offline indicator). */
  pendingUploads?: number
}

/**
 * App chrome: top bar (logo + status indicators + account menu), main slot,
 * footer with legal + status page. Status indicators are intentionally subtle —
 * green dot when online, amber dot + count when offline with pending uploads.
 *
 * Driven entirely by design tokens (`var(--bg)`, `var(--border)`, …) so it
 * inherits the studio-dark palette and stays in lock-step with the rest of the
 * primitives. The avatar menu closes on outside click.
 */
export function Layout({ children, pendingUploads = 0 }: LayoutProps) {
  const { session, signOut } = useAuth()
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  const [menuOpen, setMenuOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuWrapRef = useRef<HTMLDivElement | null>(null)

  // Auto-close the mobile drawer when the viewport grows past the lg breakpoint
  // so it can never be left open in a desktop-style layout.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setDrawerOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  // Close the account menu on any outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined
    const onDown = (e: MouseEvent) => {
      if (!menuWrapRef.current) return
      if (!menuWrapRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* SLP top bar (redesign V2): flat white tool chrome, no glass. */}
      <header
        className="sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 py-2"
        style={{
          background: 'var(--slp-panel)',
          borderBottom: '1px solid var(--slp-border)',
        }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-bold tracking-wide text-[14px] no-underline"
          style={{ color: 'var(--text)' }}
        >
          <span className="slp-lamp slp-lamp--ok" aria-hidden="true" />
          OHEAR<span style={{ color: 'var(--accent)' }}>.AI</span>
          <span
            className="slp-mono hidden md:inline"
            style={{ fontSize: 9, letterSpacing: '0.14em', color: 'var(--slp-text-dim)' }}
          >
            PROFESSIONAL REMOTE RECORDING
          </span>
        </Link>

        {/* Mobile / tablet (<lg): show only essentials inline; rest goes in the drawer */}
        <div className="inline-flex lg:hidden items-center gap-2">
          <NetworkBadge online={online} pending={pendingUploads} />
          {session ? <NotificationBell /> : null}
          <button
            type="button"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[18px] leading-none focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
            style={{
              background: 'var(--bg-3)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
            }}
          >
            <span aria-hidden="true">{drawerOpen ? '✕' : '☰'}</span>
          </button>
        </div>

        {/* Desktop (≥lg): full inline controls */}
        <div className="hidden lg:inline-flex items-center gap-3 sm:gap-4">
          <NetworkBadge online={online} pending={pendingUploads} />
          {session ? <OrgSwitcher /> : null}
          <LanguageSwitcher />
          {session ? <NotificationBell /> : null}
          {session ? (
            <div ref={menuWrapRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account menu"
                className="inline-flex items-center justify-center h-8 w-8 rounded-full font-semibold text-[13px] transition-colors focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
                style={{
                  background: 'var(--bg-2)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                }}
              >
                {initialFor(session.user.email ?? '?')}
              </button>
              {menuOpen ? (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 min-w-[200px] rounded-[var(--radius-lg)] py-1.5 z-40"
                  style={{
                    background: 'rgba(255, 255, 255, 0.96)',
                    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                    backdropFilter: 'saturate(180%) blur(20px)',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--shadow-lg)',
                  }}
                  onClick={() => setMenuOpen(false)}
                >
                  <div
                    className="px-3 py-1.5 text-[11px] uppercase tracking-wide"
                    style={{ color: 'var(--text-3)' }}
                  >
                    {session.user.email ?? 'Signed in'}
                  </div>
                  <Link
                    to="/account"
                    className="block px-3 py-1.5 text-[13px] no-underline hover:bg-[var(--bg-3)]"
                    style={{ color: 'var(--text)' }}
                  >
                    My account
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      void signOut()
                    }}
                    className="block w-full text-left px-3 py-1.5 text-[13px] hover:bg-[var(--bg-3)]"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text)',
                      font: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {drawerOpen ? (
        <div
          className="lg:hidden"
          style={{
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div className="flex flex-col gap-3 px-4 py-4">
            <div className="flex items-center justify-between">
              <span
                className="text-[12px] uppercase tracking-wide"
                style={{ color: 'var(--text-3)' }}
              >
                Language
              </span>
              <LanguageSwitcher />
            </div>
            {session ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setDrawerOpen(false)}
                  className="block w-full rounded-md px-3 py-2 text-[14px] no-underline"
                  style={{
                    background: 'var(--bg-3)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  My account
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setDrawerOpen(false)
                    void signOut()
                  }}
                  className="block w-full rounded-md px-3 py-2 text-left text-[14px]"
                  style={{
                    background: 'transparent',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    font: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  Sign out
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <main className="flex-1 w-full mx-auto px-4 sm:px-6 py-6 sm:py-8" style={{ maxWidth: 1200 }}>
        {/* Mounts the first-run product tour overlay (no DOM of its own). */}
        <TourProvider>{children}</TourProvider>
      </main>

      {/* SLP bottom status bar (redesign V2) — 24px, Status > … left, identity
          + legal links right, exactly like the session console. */}
      <footer className="slp-statusbar" data-testid="app-statusbar">
        <span>
          Status &gt;{' '}
          <b>
            {online
              ? 'All systems normal'
              : pendingUploads > 0
                ? `Offline — ${pendingUploads} upload${pendingUploads === 1 ? '' : 's'} pending`
                : 'Offline'}
          </b>
        </span>
        <span className="inline-flex items-center gap-3">
          {session?.user.email ? (
            <span>
              logged in as <b>{session.user.email}</b>
            </span>
          ) : (
            <span>(c) {new Date().getFullYear()} LERFILM INC</span>
          )}
          <a
            href="https://ohear.ai/legal/terms"
            className="no-underline hover:underline"
            style={{ color: 'var(--slp-text-dim)' }}
          >
            Terms
          </a>
          <a
            href="https://ohear.ai/legal/privacy"
            className="no-underline hover:underline"
            style={{ color: 'var(--slp-text-dim)' }}
          >
            Privacy
          </a>
          <a
            href="https://status.ohear.ai"
            className="no-underline hover:underline"
            style={{ color: 'var(--slp-text-dim)' }}
          >
            Status
          </a>
        </span>
      </footer>
    </div>
  )
}

function NetworkBadge({ online, pending }: { online: boolean; pending: number }) {
  const color = online ? 'var(--green)' : 'var(--amber)'
  const label = online ? 'Online' : pending > 0 ? `Offline · ${pending} pending` : 'Offline'
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 text-[12px] tabular-nums"
      style={{ color: 'var(--text-2)' }}
      title={label}
    >
      <span
        className={
          online
            ? 'inline-block h-2 w-2 rounded-full'
            : 'inline-block h-2 w-2 rounded-full animate-pulse'
        }
        style={{ background: color }}
      />
      {label}
    </span>
  )
}

function initialFor(email: string): string {
  const trimmed = email.trim()
  if (!trimmed) return '?'
  return trimmed[0]!.toUpperCase()
}

/**
 * Multi-org switcher. Hidden when the caller belongs to ≤1 org. Picking a
 * row writes the chosen org id to localStorage (consumed by api.ts as the
 * `x-org-id` header on every request) and invalidates the React Query
 * cache so subsequent fetches re-scope to the new org. Apple-light pill
 * style matches the language switcher next to it.
 */
function OrgSwitcher() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const orgsQ = useQuery({
    queryKey: ['me', 'orgs'],
    queryFn: () => api<{ items: MeOrg[] }>('/api/v1/me/orgs').then((r) => r.items),
    staleTime: 60 * 1000,
  })

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const orgs = orgsQ.data ?? []
  if (orgs.length <= 1) return null
  const current = orgs.find((o) => o.current) ?? orgs[0]
  if (!current) return null

  const onPick = (org: MeOrg) => {
    if (org.id === current.id) {
      setOpen(false)
      return
    }
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, org.id)
    } catch {
      // localStorage unavailable — switch is in-memory only this session.
    }
    // Best-effort persistence to the BE; failure is non-fatal.
    api('/api/v1/me/active-org', {
      method: 'POST',
      body: JSON.stringify({ org_id: org.id }),
    }).catch(() => {
      // Silent — header still wins on subsequent requests.
    })
    setOpen(false)
    // Invalidate everything; the new header on the next fetch returns
    // org-scoped data automatically.
    qc.invalidateQueries()
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Active organization: ${current.name}`}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
        style={{
          background: 'var(--bg-2)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
        }}
      >
        <span className="max-w-[140px] truncate">{current.name}</span>
        <span aria-hidden="true" style={{ color: 'var(--text-3)' }}>
          ▾
        </span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 min-w-[220px] rounded-[var(--radius-lg)] py-1.5 z-40"
          style={{
            background: 'rgba(255, 255, 255, 0.96)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            backdropFilter: 'saturate(180%) blur(20px)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div
            className="px-3 py-1.5 text-[11px] uppercase tracking-wide"
            style={{ color: 'var(--text-3)' }}
          >
            Switch organization
          </div>
          {orgs.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => onPick(org)}
              role="menuitem"
              className="block w-full text-left px-3 py-1.5 text-[13px] hover:bg-[var(--bg-3)]"
              style={{
                background: org.current ? 'var(--bg-3)' : 'transparent',
                border: 'none',
                color: 'var(--text)',
                font: 'inherit',
                cursor: 'pointer',
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="truncate">{org.name}</span>
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide"
                  style={{
                    background: 'var(--bg-3)',
                    color: 'var(--text-3)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {org.role}
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
